'use client';

import * as React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ChevronRightIcon } from 'lucide-react';
import { motion } from 'motion/react';

import { APP_NAME } from '@workspace/common/app';
import { routes } from '@workspace/routes';
import { Badge } from '@workspace/ui/components/badge';
import { buttonVariants } from '@workspace/ui/components/button';
import { ScrollArea, ScrollBar } from '@workspace/ui/components/scroll-area';
import { Separator } from '@workspace/ui/components/separator';
import {
  UnderlinedTabs,
  UnderlinedTabsContent,
  UnderlinedTabsList,
  UnderlinedTabsTrigger
} from '@workspace/ui/components/tabs';
import { cn } from '@workspace/ui/lib/utils';

import { GridSection } from '~/components/fragments/grid-section';

function HeroPill(): React.JSX.Element {
  return (
    <motion.div
      initial={{ filter: 'blur(10px)', opacity: 0, y: -20 }}
      animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="flex items-center justify-center"
    >
      <Link href={routes.marketing.Cars}>
        <Badge
          variant="outline"
          className="hover:bg-accent/50 group h-8 rounded-full px-3 text-xs font-medium shadow-sm duration-200 sm:text-sm"
        >
          <div className="w-fit py-0.5 text-center text-xs text-blue-500 sm:text-sm">
            Exclusiv
          </div>
          <Separator
            orientation="vertical"
            className="mx-2"
          />
          Mașină de schimb pe perioada reparației
          <ChevronRightIcon className="text-foreground ml-1.5 size-3 shrink-0 transition-transform group-hover:translate-x-0.5" />
        </Badge>
      </Link>
    </motion.div>
  );
}

function HeroTitle(): React.JSX.Element {
  return (
    <motion.div
      initial={{ filter: 'blur(10px)', opacity: 0, y: 20 }}
      animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.4 }}
    >
      <h1 className="mt-6 text-center text-[48px] font-bold leading-[54px] tracking-[-1.2px] [font-kerning:none] sm:text-[56px] md:text-[64px] lg:text-[76px] lg:leading-[74px] lg:tracking-[-2px]">
        Experiență de elită
        <br />
        {APP_NAME}
      </h1>
    </motion.div>
  );
}

function HeroDescription(): React.JSX.Element {
  return (
    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.4 }}
      className="text-muted-foreground mx-auto mt-3 max-w-[560px] text-balance text-center text-lg leading-[26px] sm:text-xl lg:mt-6"
    >
      Libertate de deplasare, fără stres.
      <br />
      Alegeți să conduceți un automobil premium sau beneficiați de un vehicul de 
      înlocuire acoperit integral prin RCA pe toată durata reparației, dacă sunteți partea nevinovată în accident.
      <br />
      Aveți mașina într-o unitate reparatoare?
      <br />
      Dacă autoturismul dumneavoastră este indisponibil din cauza reparațiilor, vă oferim soluții rapide de mobilitate. 
      Punem la dispoziție mașini de schimb și servicii de închiriere auto premium, cu decontare prin RCA.
    </motion.p>
  );
}

function HeroButtons(): React.JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.4 }}
      className="mx-auto flex w-full flex-col gap-2 px-7 sm:w-auto sm:flex-row sm:px-0"
    >
      <Link
        href={routes.marketing.Cars}
        className={cn(
          buttonVariants({
            variant: 'default'
          }),
          'h-10 rounded-xl sm:h-9'
        )}
      >
        Descoperiți colecția
      </Link>
      <Link
        href={routes.marketing.services.RcaReplacement}
        className={cn(
          buttonVariants({
            variant: 'secondary'
          }),
          'h-10 rounded-xl sm:h-9'
        )}
      >
        Mașină de schimb decontată prin RCA
      </Link>
      {/* <Link
        href={routes.marketing.Contact}
        className={cn(
          buttonVariants({
            variant: 'outline'
          }),
          'h-10 rounded-xl sm:h-9'
        )}
      >
        Contactați-ne
      </Link> */}
    </motion.div>
  );
}

function MainDashedGridLines(): React.JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.6, duration: 0.4 }}
    >
      <svg className="absolute left-[16.85%] top-0 hidden h-full w-px [mask-image:linear-gradient(to_bottom,#0000,#000_128px,#000_calc(100%-24px),#0000)] lg:block">
        <line
          x1="0.5"
          y1="0"
          x2="0.5"
          y2="100%"
          strokeLinecap="round"
          strokeDasharray="5 5"
          stroke="hsl(var(--border))"
        />
      </svg>
      <svg className="absolute right-[16.85%] top-0 hidden h-full w-px [mask-image:linear-gradient(to_bottom,#0000,#000_128px,#000_calc(100%-24px),#0000)] lg:block">
        <line
          x1="0.5"
          y1="0"
          x2="0.5"
          y2="100%"
          strokeLinecap="round"
          strokeDasharray="5 5"
          stroke="hsl(var(--border))"
        />
      </svg>
      <svg className="absolute bottom-[52px] left-[calc(50%-50vw)] hidden h-px w-screen [mask-image:linear-gradient(to_right,#0000,#000_100px,#000_calc(100%-100px),#0000)] lg:block">
        <line
          x1="0"
          y1="0.5"
          x2="100%"
          y2="0.5"
          strokeLinecap="round"
          strokeDasharray="5 5"
          stroke="hsl(var(--border))"
        />
      </svg>
    </motion.div>
  );
}

function SupportiveDashedGridLines(): React.JSX.Element {
  return (
    <>
      <svg className="absolute left-[calc(50%-50vw)] top-[-25px] z-10 hidden h-px w-screen [mask-image:linear-gradient(to_right,#0000,#000_100px,#000_calc(100%-100px),#0000)] lg:block">
        <line
          x1="0"
          y1="0.5"
          x2="100%"
          y2="0.5"
          strokeLinecap="round"
          strokeDasharray="5 5"
          stroke="hsl(var(--border))"
        />
      </svg>
      <svg className="absolute left-[calc(50%-50vw)] top-0 z-10 hidden h-px w-screen [mask-image:linear-gradient(to_right,#0000,#000_100px,#000_calc(100%-100px),#0000)] lg:block">
        <line
          x1="0"
          y1="0.5"
          x2="100%"
          y2="0.5"
          strokeLinecap="round"
          strokeDasharray="5 5"
          stroke="hsl(var(--border))"
        />
      </svg>
      <svg className="absolute left-[calc(50%-50vw)] top-[52px] z-10 hidden h-px w-screen [mask-image:linear-gradient(to_right,#0000,#000_100px,#000_calc(100%-100px),#0000)] lg:block">
        <line
          x1="0"
          y1="0.5"
          x2="100%"
          y2="0.5"
          strokeLinecap="round"
          strokeDasharray="5 5"
          stroke="hsl(var(--border))"
        />
      </svg>
    </>
  );
}

function HeroIllustration(): React.JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.6, duration: 0.4 }}
      className="relative mt-3 lg:mt-6"
    >
      <UnderlinedTabs defaultValue="executive">
        <ScrollArea className="max-w-[100vw] lg:max-w-none">
          <UnderlinedTabsList className="relative z-20 mb-6 flex h-fit flex-row flex-wrap justify-center md:flex-nowrap">
            <UnderlinedTabsTrigger
              value="executive"
              className="mx-1 px-2.5 sm:mx-2 sm:px-3"
            >
              Executive
            </UnderlinedTabsTrigger>
            <UnderlinedTabsTrigger
              value="luxury"
              className="mx-1 px-2.5 sm:mx-2 sm:px-3"
            >
              Luxury
            </UnderlinedTabsTrigger>
            <UnderlinedTabsTrigger
              value="standard"
              className="mx-1 px-2.5 sm:mx-2 sm:px-3"
            >
              Standard
            </UnderlinedTabsTrigger>
          </UnderlinedTabsList>
          <ScrollBar
            orientation="horizontal"
            className="invisible"
          />
        </ScrollArea>
        <div className="dark:bg-background relative mb-1 w-full rounded-xl dark:border-none">
          <SupportiveDashedGridLines />
          <div className="bg-background relative z-20">
            <UnderlinedTabsContent value="executive">
              <Image
                priority
                quality={100}
                src="/assets/hero/hero.webp"
                width="1328"
                height="727"
                alt="Colecția Executive - mașini de lux premium"
                className="rounded-xl border shadow"
              />
            </UnderlinedTabsContent>
            <UnderlinedTabsContent value="luxury">
              <Image
                priority
                quality={100}
                src="/assets/cars/cars-5.jpg"
                width="1328"
                height="727"
                alt="Colecția Luxury - mașini de lux"
                className="rounded-xl border shadow"
              />
            </UnderlinedTabsContent>
            <UnderlinedTabsContent value="standard">
              <Image
                priority
                quality={100}
                src="/assets/cars/cars-2.jpg"
                width="1328"
                height="727"
                alt="Colecția Standard - mașini premium"
                className="rounded-xl border shadow"
              />
            </UnderlinedTabsContent>
          </div>
        </div>
      </UnderlinedTabs>
    </motion.div>
  );
}

export function Hero(): React.JSX.Element {
  return (
    <GridSection className="overflow-x-hidden">
      <MainDashedGridLines />
      <div className="mx-auto mt-16 flex flex-col gap-6 px-2 sm:mt-20 sm:px-1 md:mt-24 lg:mt-32">
        <div className="gap-2">
          <HeroPill />
          <HeroTitle />
        </div>
        <HeroDescription />
        <HeroButtons />
        <HeroIllustration />
      </div>
    </GridSection>
  );
}
